// src/pages/admin/AdminPanels.jsx
//
// All operational panels: Users, Moderation, Support, Analytics, System.
// Includes the new FeaturesPanel (share-rewards on/off toggle).
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Search, ChevronLeft, ChevronRight, Download, RefreshCw,
  Loader2, User as UserIcon, X, UserCheck, Ban, Crown,
  Trash2, ExternalLink, Check, MessageSquare, Send, Reply,
  Gem, Coins, Sparkles,
} from "lucide-react";

import {
  KpiCard, SectionCard, LineChart, BarChart, timeAgo, fmtDate,
} from "./AdminShared";

// =====================================================================
// Constants used only by Users + Moderation
// =====================================================================
const OFFENCE_KEYS = ["nudity", "violence", "minor", "racist", "stigma", "sexual", "inappropriate"];

const OFFENCE_LABEL = {
  nudity: "Nudity", violence: "Violence", minor: "Minor",
  racist: "Racist content", stigma: "Stigma", sexual: "Sexual",
  inappropriate: "Inappropriate",
};

const USER_FILTERS = [
  { key: "", label: "All" },
  { key: "verified", label: "Verified" },
  { key: "unverified", label: "Unverified" },
  { key: "premium", label: "Premium" },
  { key: "active_24h", label: "Active 24h" },
  { key: "inactive_30d", label: "Inactive 30d" },
  { key: "banned", label: "Banned" },
  { key: "admin", label: "Admin" },
  { key: "deleted", label: "Deleted" },
];

const USER_SORTS = [
  { key: "newest", label: "Newest" },
  { key: "oldest", label: "Oldest" },
  { key: "active", label: "Last active" },
  { key: "diamonds", label: "Most diamonds" },
  { key: "tokens", label: "Most tokens" },
];

// =====================================================================
// USERS TAB
// =====================================================================
export function UsersTab() {
  const [query, setQuery] = useState("");
  const [filterBy, setFilterBy] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ users: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [openUser, setOpenUser] = useState(null);
  const searchTimeout = useRef(null);

  const load = useCallback(async (overridePage) => {
    setLoading(true);
    try {
      const res = await api.get("/admin/users/list", {
        params: {
          query: query || undefined,
          filter_by: filterBy || undefined,
          sort_by: sortBy,
          page: overridePage ?? page,
          limit: 50,
        },
      });
      setResult(res.data);
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [query, filterBy, sortBy, page]);

  useEffect(() => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setPage(1);
      load(1);
    }, 300);
    return () => clearTimeout(searchTimeout.current);
  }, [query, filterBy, sortBy, load]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === result.users.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(result.users.map((u) => u.user_id)));
    }
  };

  const bulk = async (action) => {
    if (selected.size === 0) return;
    const labels = {
      verify: `Verify ${selected.size} user(s)?`,
      unverify: `Unverify ${selected.size} user(s)?`,
      ban: `Ban ${selected.size} user(s)?`,
      unban: `Unban ${selected.size} user(s)?`,
    };
    if (!window.confirm(labels[action] || `${action}?`)) return;
    try {
      await api.post("/admin/users/bulk-action", {
        user_ids: Array.from(selected),
        action,
        reason: action === "ban" ? "Bulk action from admin panel" : undefined,
      });
      toast.success(`${selected.size} user(s) updated`);
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk action failed");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/users/export/csv", {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `haven_users_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Users</h2>
          <p className="text-xs text-[#6B6B70]">
            {result.total.toLocaleString()} total · {result.pages} page{result.pages !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportCsv}
            className="text-xs px-3 py-2 rounded-lg border border-[#E7E5E0] hover:bg-[#F5F3EE] flex items-center gap-1.5"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          <button
            onClick={() => load(page)}
            className="text-xs px-3 py-2 rounded-lg border border-[#E7E5E0] hover:bg-[#F5F3EE] flex items-center gap-1.5"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E7E5E0] rounded-2xl p-4 space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by email, name, or user_id…"
              className="neo-input pl-9"
            />
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="neo-input w-auto min-w-[160px]"
          >
            {USER_SORTS.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {USER_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilterBy(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                filterBy === f.key
                  ? "bg-purple-600 text-white"
                  : "bg-white border border-[#E7E5E0] text-[#0F0F10] hover:bg-[#F5F3EE]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {selected.size > 0 && (
        <div className="sticky top-20 z-20 flex flex-wrap items-center gap-2 p-3 bg-purple-50 border border-purple-200 rounded-xl">
          <span className="text-sm font-medium text-purple-900">
            {selected.size} selected
          </span>
          <div className="flex gap-2 ml-auto flex-wrap">
            <button onClick={() => bulk("verify")} className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700">
              Verify
            </button>
            <button onClick={() => bulk("unverify")} className="text-xs px-3 py-1.5 rounded-lg bg-gray-600 text-white hover:bg-gray-700">
              Unverify
            </button>
            <button onClick={() => bulk("ban")} className="text-xs px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700">
              Ban
            </button>
            <button onClick={() => bulk("unban")} className="text-xs px-3 py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700">
              Unban
            </button>
            <button onClick={() => setSelected(new Set())} className="text-xs px-3 py-1.5 rounded-lg border border-purple-200 text-purple-700 hover:bg-purple-100">
              Clear
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E7E5E0] rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-purple-600 mx-auto" />
          </div>
        ) : result.users.length === 0 ? (
          <div className="p-12 text-center text-sm text-[#6B6B70]">
            No users match your search.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F3EE] text-xs text-[#6B6B70] uppercase">
                <tr>
                  <th className="w-10 p-3">
                    <input
                      type="checkbox"
                      checked={selected.size === result.users.length && result.users.length > 0}
                      onChange={selectAll}
                      className="accent-purple-600"
                    />
                  </th>
                  <th className="text-left p-3">User</th>
                  <th className="text-left p-3 hidden md:table-cell">Status</th>
                  <th className="text-right p-3 hidden sm:table-cell">Tokens</th>
                  <th className="text-right p-3 hidden sm:table-cell">Diamonds</th>
                  <th className="text-left p-3 hidden lg:table-cell">Signed up</th>
                  <th className="text-left p-3 hidden lg:table-cell">Last active</th>
                  <th className="text-left p-3 hidden lg:table-cell">Platform</th>
                  <th className="w-20 p-3"></th>
                </tr>
              </thead>
              <tbody>
                {result.users.map((u) => (
                  <tr
                    key={u.user_id}
                    className="border-t border-[#E7E5E0] hover:bg-[#FAFAF7] cursor-pointer"
                    onClick={() => setOpenUser(u.user_id)}
                  >
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(u.user_id)}
                        onChange={() => toggleSelect(u.user_id)}
                        className="accent-purple-600"
                      />
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-8 h-8 rounded-full overflow-hidden bg-[#F5F3EE] flex-shrink-0">
                          {u.picture ? (
                            <img src={u.picture} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full grid place-items-center text-gray-400">
                              <UserIcon className="w-3.5 h-3.5" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium truncate">{u.name || u.email}</div>
                          <div className="text-xs text-[#6B6B70] truncate font-mono">{u.user_id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 hidden md:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {u.verified && <span className="px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-medium">Verified</span>}
                        {u.premium_tier && <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-medium capitalize">{u.premium_tier}</span>}
                        {u.is_admin && <span className="px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 text-[10px] font-medium">Admin</span>}
                        {u.banned && <span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-medium">Banned</span>}
                        {u.deleted && <span className="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px] font-medium">Deleted</span>}
                      </div>
                    </td>
                    <td className="p-3 text-right hidden sm:table-cell tabular-nums">{u.tokens ?? 0}</td>
                    <td className="p-3 text-right hidden sm:table-cell tabular-nums">{u.diamonds ?? 0}</td>
                    <td className="p-3 text-xs text-[#6B6B70] hidden lg:table-cell">{fmtDate(u.created_at)}</td>
                    <td className="p-3 text-xs text-[#6B6B70] hidden lg:table-cell">{timeAgo(u.last_active)}</td>
                    <td className="p-3 text-xs text-[#6B6B70] hidden lg:table-cell">
                      {u.last_platform === 'web' ? (
                        <span className="inline-flex items-center gap-1">🌐 Web</span>
                      ) : (
                        <span className="inline-flex items-center gap-1">📱 App</span>
                      )}
                    </td>
                    <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => setOpenUser(u.user_id)}
                        className="text-xs text-purple-600 hover:underline"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {result.pages > 1 && (
        <div className="flex items-center justify-between gap-2">
          <button
            disabled={page <= 1}
            onClick={() => { setPage(page - 1); load(page - 1); }}
            className="text-sm px-3 py-2 rounded-lg border border-[#E7E5E0] hover:bg-[#F5F3EE] disabled:opacity-40 flex items-center gap-1"
          >
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>
          <span className="text-sm text-[#6B6B70]">
            Page {page} of {result.pages}
          </span>
          <button
            disabled={page >= result.pages}
            onClick={() => { setPage(page + 1); load(page + 1); }}
            className="text-sm px-3 py-2 rounded-lg border border-[#E7E5E0] hover:bg-[#F5F3EE] disabled:opacity-40 flex items-center gap-1"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {openUser && (
        <UserDetailDrawer
          userId={openUser}
          onClose={() => { setOpenUser(null); load(); }}
        />
      )}
    </div>
  );
}

function UserDetailDrawer({ userId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokensAmt, setTokensAmt] = useState(50);
  const [diamondsAmt, setDiamondsAmt] = useState(50);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/users/${userId}/full`);
      setDetail(res.data);
    } catch {
      toast.error("Failed to load user");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const toggleVerify = async () => {
    if (!detail) return;
    try {
      await api.post("/admin/verify-user", {
        user_id: userId,
        verified: !detail.user.verified,
      });
      toast.success("Verification updated");
      load();
    } catch { toast.error("Failed"); }
  };

  const toggleBan = async () => {
    if (!detail) return;
    try {
      if (detail.user.banned) {
        await api.post("/admin/unban", { user_id: userId });
        toast.success("Unbanned");
      } else {
        if (!window.confirm("Ban this user?")) return;
        await api.post("/admin/ban", {
          user_id: userId,
          reason: "Banned from admin panel",
          duration_days: null,
        });
        toast.success("Banned");
      }
      load();
    } catch { toast.error("Failed"); }
  };

  const grantTokens = async () => {
    try {
      await api.post(`/admin/users/${userId}/grant-tokens`, { amount: parseInt(tokensAmt, 10) });
      toast.success("Tokens updated");
      load();
    } catch { toast.error("Failed"); }
  };

  const grantDiamonds = async () => {
    try {
      await api.post(`/admin/users/${userId}/grant-diamonds`, { amount: parseInt(diamondsAmt, 10) });
      toast.success("Diamonds updated");
      load();
    } catch { toast.error("Failed"); }
  };

  const setPremium = async (tier) => {
    try {
      await api.post(`/admin/users/${userId}/grant-premium`, { tier });
      toast.success(tier ? `${tier} granted` : "Premium removed");
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-end" onClick={onClose}>
      <div
        className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-[#E7E5E0] px-5 py-4 flex items-center justify-between z-10">
          <h3 className="font-semibold">User detail</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F5F3EE] rounded-lg">
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-purple-600 mx-auto" />
          </div>
        ) : !detail ? null : (
          <div className="p-5 space-y-5">
            <div className="flex items-start gap-3">
              <div className="w-14 h-14 rounded-full overflow-hidden bg-[#F5F3EE] flex-shrink-0">
                {detail.user.picture ? (
                  <img src={detail.user.picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full grid place-items-center text-gray-400">
                    <UserIcon className="w-6 h-6" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-lg truncate">
                  {detail.user.name || detail.user.email}
                </div>
                <div className="text-sm text-[#6B6B70] truncate">{detail.user.email}</div>
                <div className="text-xs text-[#6B6B70] font-mono truncate mt-0.5">{detail.user.user_id}</div>
                <div className="text-xs text-[#6B6B70] mt-1">
                  <strong>Last login:</strong>{" "}
                  {detail.user.last_platform === 'web' ? '🌐 Web' : '📱 App'}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div className="bg-[#F5F3EE] rounded-lg p-2 text-center">
                <div className="text-xs text-[#6B6B70]">Matches</div>
                <div className="font-bold tabular-nums">{detail.stats.matches}</div>
              </div>
              <div className="bg-[#F5F3EE] rounded-lg p-2 text-center">
                <div className="text-xs text-[#6B6B70]">Messages</div>
                <div className="font-bold tabular-nums">{detail.stats.messages_sent}</div>
              </div>
              <div className="bg-[#F5F3EE] rounded-lg p-2 text-center">
                <div className="text-xs text-[#6B6B70]">Stories</div>
                <div className="font-bold tabular-nums">{detail.stats.stories}</div>
              </div>
              <div className="bg-[#F5F3EE] rounded-lg p-2 text-center">
                <div className="text-xs text-[#6B6B70]">Reports ↓</div>
                <div className="font-bold tabular-nums text-red-600">{detail.stats.reports_against}</div>
              </div>
              <div className="bg-[#F5F3EE] rounded-lg p-2 text-center">
                <div className="text-xs text-[#6B6B70]">Reports ↑</div>
                <div className="font-bold tabular-nums">{detail.stats.reports_by}</div>
              </div>
              <div className="bg-[#F5F3EE] rounded-lg p-2 text-center">
                <div className="text-xs text-[#6B6B70]">Purchases</div>
                <div className="font-bold tabular-nums">{detail.stats.purchases}</div>
              </div>
            </div>

            <div className="space-y-2">
              <button
                onClick={toggleVerify}
                className={`w-full flex items-center justify-between p-3 rounded-xl border ${
                  detail.user.verified
                    ? "border-blue-200 bg-blue-50"
                    : "border-[#E7E5E0] hover:bg-[#F5F3EE]"
                }`}
              >
                <span className="flex items-center gap-2 text-sm font-medium">
                  <UserCheck className="w-4 h-4" />
                  {detail.user.verified ? "Verified" : "Not verified"}
                </span>
                <span className="text-xs text-[#6B6B70]">
                  {detail.user.verified ? "Click to unverify" : "Click to verify"}
                </span>
              </button>
              <button
                onClick={toggleBan}
                className={`w-full flex items-center justify-between p-3 rounded-xl border ${
                  detail.user.banned
                    ? "border-red-200 bg-red-50"
                    : "border-[#E7E5E0] hover:bg-[#F5F3EE]"
                }`}
              >
                <span className="flex items-center gap-2 text-sm font-medium">
                  <Ban className="w-4 h-4" />
                  {detail.user.banned ? "Banned" : "Active"}
                </span>
                <span className="text-xs text-[#6B6B70]">
                  {detail.user.banned ? "Click to unban" : "Click to ban"}
                </span>
              </button>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase text-[#6B6B70] mb-2">Premium</div>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => setPremium("gold")}
                  className={`p-2 rounded-lg border text-xs font-medium ${
                    detail.user.premium_tier === "gold"
                      ? "border-amber-400 bg-amber-50 text-amber-700"
                      : "border-[#E7E5E0] hover:bg-[#F5F3EE]"
                  }`}
                >
                  <Crown className="w-3.5 h-3.5 mx-auto mb-0.5" /> Gold
                </button>
                <button
                  onClick={() => setPremium("platinum")}
                  className={`p-2 rounded-lg border text-xs font-medium ${
                    detail.user.premium_tier === "platinum"
                      ? "border-purple-400 bg-purple-50 text-purple-700"
                      : "border-[#E7E5E0] hover:bg-[#F5F3EE]"
                  }`}
                >
                  <Crown className="w-3.5 h-3.5 mx-auto mb-0.5" /> Platinum
                </button>
                <button
                  onClick={() => setPremium(null)}
                  className="p-2 rounded-lg border border-[#E7E5E0] text-xs font-medium hover:bg-[#F5F3EE]"
                >
                  <X className="w-3.5 h-3.5 mx-auto mb-0.5" /> None
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs font-semibold uppercase text-[#6B6B70] mb-1.5">Tokens</div>
                <div className="flex gap-1.5">
                  <input
                    type="number"
                    value={tokensAmt}
                    onChange={(e) => setTokensAmt(e.target.value)}
                    className="neo-input w-full"
                  />
                  <button
                    onClick={grantTokens}
                    className="neo-btn bg-amber-500 border-amber-500 text-xs whitespace-nowrap"
                  >
                    Grant
                  </button>
                </div>
                <p className="text-[10px] text-[#6B6B70] mt-1">
                  Balance: {detail.user.tokens ?? 0} (negative to deduct)
                </p>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase text-[#6B6B70] mb-1.5">Diamonds</div>
                <div className="flex gap-1.5">
                  <input
                    type="number"
                    value={diamondsAmt}
                    onChange={(e) => setDiamondsAmt(e.target.value)}
                    className="neo-input w-full"
                  />
                  <button
                    onClick={grantDiamonds}
                    className="neo-btn bg-purple-600 border-purple-600 text-xs whitespace-nowrap"
                  >
                    Grant
                  </button>
                </div>
                <p className="text-[10px] text-[#6B6B70] mt-1">
                  Balance: {detail.user.diamonds ?? 0}
                </p>
              </div>
            </div>

            {detail.recent_purchases?.length > 0 && (
              <div>
                <div className="text-xs font-semibold uppercase text-[#6B6B70] mb-2">Recent purchases</div>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {detail.recent_purchases.map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-xs p-2 bg-[#F5F3EE] rounded-lg">
                      <span className="truncate">{p.item}</span>
                      <span className="text-[#6B6B70] flex-shrink-0 ml-2">
                        {p.diamond_cost > 0 ? `${p.diamond_cost}💎` : "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {detail.profile && (
              <div>
                <div className="text-xs font-semibold uppercase text-[#6B6B70] mb-2">Profile</div>
                <div className="text-xs space-y-1 text-[#262626]">
                  {detail.profile.gender && <div><strong>Gender:</strong> {detail.profile.gender}</div>}
                  {detail.profile.health_status && <div><strong>Health:</strong> {detail.profile.health_status}</div>}
                  {detail.profile.country && <div><strong>Location:</strong> {detail.profile.city ? `${detail.profile.city}, ` : ""}{detail.profile.country}</div>}
                  {detail.profile.display_name && <div><strong>Display name:</strong> {detail.profile.display_name}</div>}
                  {detail.profile.bio && <div className="line-clamp-3"><strong>Bio:</strong> {detail.profile.bio}</div>}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// =====================================================================
// MODERATION TAB
// =====================================================================
export function ModerationTab({ initialSub }) {
  const [sub, setSub] = useState(initialSub || "reports");
  useEffect(() => { if (initialSub) setSub(initialSub); }, [initialSub]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1.5">
        {[
          { key: "reports", label: "Reports" },
          { key: "images", label: "Image queue" },
          { key: "stories", label: "Stories" },
          { key: "shares", label: "Share claims" },
        ].map((s) => (
          <button
            key={s.key}
            onClick={() => setSub(s.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium ${
              sub === s.key ? "bg-purple-600 text-white" : "bg-white border border-[#E7E5E0] hover:bg-[#F5F3EE]"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>
      {sub === "reports" && <ReportsPanel />}
      {sub === "images" && <ImageQueuePanel />}
      {sub === "stories" && <StoriesPanel />}
      {sub === "shares" && <ShareClaimsPanel />}
    </div>
  );
}

function ReportsPanel() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offenceFilter, setOffenceFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/reports-detailed");
      const seen = new Set();
      const deduped = [];
      for (const r of (Array.isArray(res.data) ? res.data : [])) {
        const m = r.reason?.match(/\(image (\d+)\)/);
        const key = r.reported_user_id + (m ? `-img-${m[1]}` : r.story_id ? `-story-${r.story_id}` : "-u");
        if (!seen.has(key)) {
          seen.add(key);
          deduped.push(r);
        }
      }
      setReports(deduped);
    } catch {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = offenceFilter
    ? reports.filter((r) => (r.reason_clean || r.reason?.split(" (")[0]) === offenceFilter)
    : reports;

  const resolve = async (id) => {
    try {
      await api.post(`/admin/reports/${id}/resolve`);
      setReports((prev) => prev.filter((r) => r.report_id !== id));
      toast.success("Report resolved");
    } catch { toast.error("Failed"); }
  };

  const deleteContent = async (r) => {
    if (!window.confirm("Delete this content and warn the user?")) return;
    try {
      const endpoint = r.type === "story"
        ? `/admin/reports/${r.report_id}/delete-story`
        : `/admin/reports/${r.report_id}/delete-photo`;
      await api.delete(endpoint);
      setReports((prev) => prev.filter((r2) => r2.report_id !== r.report_id));
      toast.success("Content removed");
    } catch { toast.error("Failed"); }
  };

  const suspend = async (userId) => {
    if (!window.confirm("Suspend this user permanently?")) return;
    try {
      await api.post("/admin/suspend", {
        user_id: userId,
        reason: "Violation reported by users",
      });
      setReports((prev) => prev.filter((r) => r.reported_user_id !== userId));
      toast.success("User suspended");
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5">
        <button
          onClick={() => setOffenceFilter("")}
          className={`px-3 py-1.5 rounded-full text-xs font-medium ${
            !offenceFilter ? "bg-purple-600 text-white" : "bg-white border border-[#E7E5E0]"
          }`}
        >
          All ({reports.length})
        </button>
        {OFFENCE_KEYS.map((k) => {
          const count = reports.filter((r) => (r.reason_clean || r.reason?.split(" (")[0]) === k).length;
          return (
            <button
              key={k}
              onClick={() => setOffenceFilter(k)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                offenceFilter === k ? "bg-purple-600 text-white" : "bg-white border border-[#E7E5E0]"
              }`}
            >
              {OFFENCE_LABEL[k]} ({count})
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-purple-600 mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-[#E7E5E0] rounded-2xl p-10 text-center text-[#6B6B70]">
          🎉 No reports {offenceFilter ? `for ${OFFENCE_LABEL[offenceFilter]}` : ""}.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => (
            <div key={r.report_id} className="bg-white border border-[#E7E5E0] rounded-2xl p-4 flex flex-col md:flex-row md:items-start gap-4">
              <div className="flex-1 min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                    r.type === "story" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"
                  }`}>
                    {r.type === "story" ? "Story" : "Photo"}
                  </span>
                  <span className="font-medium text-sm truncate">{r.reported_name}</span>
                  <span className="text-xs text-[#6B6B70] truncate">({r.reported_email})</span>
                </div>
                <div className="text-xs text-[#6B6B70]">
                  Reported by <strong>{r.reporter_name}</strong> · {OFFENCE_LABEL[r.reason_clean] || r.reason_clean}
                </div>
                <div className="text-xs text-[#6B6B70]">{new Date(r.created_at).toLocaleString()}</div>
                {r.type === "photo" && r.image_url && (
                  <img src={r.image_url} alt="" className="mt-2 w-32 h-32 object-cover rounded-lg border" />
                )}
                {r.type === "story" && r.story_content && (
                  <div className="mt-2 p-2 bg-[#F5F3EE] rounded-lg text-xs max-w-lg line-clamp-3">
                    {r.story_content}
                  </div>
                )}
              </div>
              <div className="flex flex-row md:flex-col gap-2 flex-shrink-0">
                {(r.type === "photo" && r.image_url) || (r.type === "story" && r.story_id) ? (
                  <button onClick={() => deleteContent(r)} className="text-xs px-3 py-1.5 rounded-lg bg-red-500 text-white hover:bg-red-600">
                    Delete content
                  </button>
                ) : null}
                <button onClick={() => suspend(r.reported_user_id)} className="text-xs px-3 py-1.5 rounded-lg bg-red-700 text-white hover:bg-red-800">
                  Suspend user
                </button>
                <button onClick={() => resolve(r.report_id)} className="text-xs px-3 py-1.5 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300">
                  Dismiss
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ImageQueuePanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/image-queue");
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (userId, type, idx) => {
    if (!window.confirm(`Delete this ${type} image?`)) return;
    try {
      await api.delete(`/admin/images/${userId}/${type}/${idx}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Failed"); }
  };

  if (loading) return <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-purple-600 mx-auto" /></div>;
  if (items.length === 0) return <div className="bg-white border rounded-2xl p-10 text-center text-[#6B6B70]">No recent images.</div>;

  return (
    <div className="space-y-4">
      {items.map((p) => (
        <div key={p.user_id} className="bg-white border border-[#E7E5E0] rounded-2xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-medium text-sm">{p.display_name}</div>
              <div className="text-xs text-[#6B6B70] font-mono">{p.user_id}</div>
            </div>
            <div className="text-xs text-[#6B6B70]">Updated {fmtDate(p.updated_at)}</div>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {p.profile_image && (
              <div className="relative group aspect-square rounded-lg overflow-hidden bg-[#F5F3EE]">
                <img src={p.profile_image} alt="" className="w-full h-full object-cover" />
                <button
                  onClick={() => del(p.user_id, "profile", 0)}
                  className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100"
                >
                  <Trash2 className="w-5 h-5 text-white" />
                </button>
              </div>
            )}
            {(p.gallery_images || []).map((url, idx) => (
              <div key={idx} className="relative group aspect-square rounded-lg overflow-hidden bg-[#F5F3EE]">
                <img src={url} alt="" className="w-full h-full object-cover" />
                <button
                  onClick={() => del(p.user_id, "gallery", idx)}
                  className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100"
                >
                  <Trash2 className="w-5 h-5 text-white" />
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function StoriesPanel() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/stories");
      setStories(Array.isArray(res.data) ? res.data : []);
    } catch { toast.error("Failed"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this story permanently?")) return;
    try {
      await api.delete(`/admin/stories/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Failed"); }
  };

  if (loading) return <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-purple-600 mx-auto" /></div>;
  if (stories.length === 0) return <div className="bg-white border rounded-2xl p-10 text-center text-[#6B6B70]">No stories.</div>;

  return (
    <div className="space-y-3">
      {stories.map((s) => (
        <div key={s.story_id} className="bg-white border border-[#E7E5E0] rounded-xl p-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-medium">{s.author_name}</div>
            <div className="text-xs text-[#6B6B70]">{s.category} · {fmtDate(s.created_at)}</div>
            {s.title && <div className="text-sm font-medium mt-1">{s.title}</div>}
            <p className="text-sm mt-1 line-clamp-3">{s.content}</p>
          </div>
          <button onClick={() => del(s.story_id)} className="text-red-600 p-2 hover:bg-red-50 rounded-lg flex-shrink-0">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
}

function ShareClaimsPanel() {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("pending");
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/share-claims?status=${status}`);
      setClaims(Array.isArray(res.data) ? res.data : []);
    } catch { toast.error("Failed"); }
    finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    if (!window.confirm("Approve this claim and award 100 tokens?")) return;
    try {
      await api.post(`/admin/share-claims/${id}/approve`, { admin_notes: "" });
      toast.success("Approved — 100 tokens awarded");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const reject = async () => {
    if (!rejectId || !rejectReason.trim()) return;
    try {
      await api.post(`/admin/share-claims/${rejectId}/reject`, { admin_notes: rejectReason.trim() });
      toast.success("Rejected");
      setRejectId(null);
      setRejectReason("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-1.5">
        {["pending", "approved", "rejected"].map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize ${
              status === s ? "bg-purple-600 text-white" : "bg-white border border-[#E7E5E0]"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-purple-600 mx-auto" /></div>
      ) : claims.length === 0 ? (
        <div className="bg-white border rounded-2xl p-10 text-center text-[#6B6B70]">
          No {status} claims.
        </div>
      ) : (
        claims.map((c) => (
          <div key={c.claim_id} className="bg-white border border-[#E7E5E0] rounded-2xl p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-100 text-purple-700 uppercase">
                    {c.platform}
                  </span>
                  <span className="text-xs text-[#6B6B70]">{new Date(c.created_at).toLocaleString()}</span>
                </div>
                <div className="font-semibold text-sm truncate mt-1">{c.user_name || c.user_email}</div>
                <div className="text-xs text-[#6B6B70] font-mono truncate">{c.user_id}</div>
                {c.invite_code && (
                  <div className="text-xs text-[#6B6B70] mt-1">
                    Invite: <span className="font-mono font-semibold text-purple-700">{c.invite_code}</span>
                  </div>
                )}
              </div>
              <a
                href={c.post_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Open post
              </a>
            </div>
            <div className="text-xs font-mono break-all p-2 rounded-lg bg-[#F5F3EE]">{c.post_url}</div>
            {status === "pending" && (
              <div className="flex gap-2">
                <button onClick={() => approve(c.claim_id)} className="text-xs px-3 py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700 flex items-center gap-1">
                  <Check className="w-3.5 h-3.5" /> Approve
                </button>
                <button onClick={() => setRejectId(c.claim_id)} className="text-xs px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 flex items-center gap-1">
                  <X className="w-3.5 h-3.5" /> Reject
                </button>
              </div>
            )}
            {c.admin_notes && (
              <div className="text-xs p-2 bg-red-50 border border-red-200 rounded-lg text-red-800">
                <strong>Notes:</strong> {c.admin_notes}
              </div>
            )}
          </div>
        ))
      )}

      {rejectId && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setRejectId(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold mb-2">Rejection reason</h3>
            <p className="text-sm text-[#6B6B70] mb-3">The user will see this message.</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="neo-input h-24 resize-none"
              placeholder="e.g. Post doesn't contain the invite code."
            />
            <div className="flex gap-2 mt-3">
              <button onClick={() => setRejectId(null)} className="flex-1 py-2.5 rounded-xl border border-[#E7E5E0] text-sm">Cancel</button>
              <button onClick={reject} className="flex-1 py-2.5 rounded-xl bg-red-600 text-white text-sm">Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =====================================================================
// SUPPORT TAB
// =====================================================================
export function SupportTab({ initialSub }) {
  const [sub, setSub] = useState(initialSub || "tickets");
  useEffect(() => { if (initialSub) setSub(initialSub); }, [initialSub]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1.5">
        {[
          { key: "tickets", label: "Support tickets" },
          { key: "feedback", label: "Feedback" },
          { key: "dsr", label: "POPIA DSR" },
        ].map((s) => (
          <button
            key={s.key}
            onClick={() => setSub(s.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium ${
              sub === s.key ? "bg-purple-600 text-white" : "bg-white border border-[#E7E5E0] hover:bg-[#F5F3EE]"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>
      {sub === "tickets" && <TicketsPanel />}
      {sub === "feedback" && <FeedbackPanel />}
      {sub === "dsr" && <DsrPanel />}
    </div>
  );
}

function TicketsPanel() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/support/tickets");
      setTickets(res.data || []);
    } catch { toast.error("Failed"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openTicket = async (t) => {
    setSelected(t);
    try {
      const res = await api.get(`/support/tickets/${t.ticket_id}/messages`);
      setMessages(res.data || []);
    } catch { toast.error("Failed to load messages"); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    setSending(true);
    try {
      await api.post(`/support/tickets/${selected.ticket_id}/messages`, { content: reply.trim() });
      setReply("");
      const res = await api.get(`/support/tickets/${selected.ticket_id}/messages`);
      setMessages(res.data || []);
    } catch { toast.error("Failed"); }
    finally { setSending(false); }
  };

  const close = async (id) => {
    if (!window.confirm("Close this ticket?")) return;
    try {
      await api.put(`/support/tickets/${id}/close`);
      toast.success("Closed");
      setSelected(null);
      load();
    } catch { toast.error("Failed"); }
  };

  if (loading) return <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-purple-600 mx-auto" /></div>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="bg-white border border-[#E7E5E0] rounded-2xl p-3 space-y-1 md:col-span-1 max-h-[600px] overflow-y-auto">
        {tickets.length === 0 && <p className="text-sm text-[#6B6B70] p-4 text-center">No tickets</p>}
        {tickets.map((t) => (
          <button
            key={t.ticket_id}
            onClick={() => openTicket(t)}
            className={`w-full text-left p-3 rounded-lg text-sm transition-colors ${
              selected?.ticket_id === t.ticket_id ? "bg-purple-50 border border-purple-200" : "hover:bg-[#F5F3EE] border border-transparent"
            }`}
          >
            <div className="font-medium truncate">{t.subject}</div>
            <div className="flex items-center gap-2 text-xs text-[#6B6B70] mt-1">
              <span className={`w-1.5 h-1.5 rounded-full ${t.status === "open" ? "bg-green-500" : "bg-gray-400"}`} />
              {t.status} · {new Date(t.updated_at).toLocaleDateString()}
            </div>
          </button>
        ))}
      </div>

      <div className="bg-white border border-[#E7E5E0] rounded-2xl p-4 md:col-span-2 flex flex-col min-h-[400px]">
        {!selected ? (
          <div className="flex-1 flex flex-col items-center justify-center text-[#6B6B70] text-sm">
            <MessageSquare className="w-10 h-10 opacity-20 mb-2" />
            Select a ticket
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between border-b border-[#E7E5E0] pb-3 mb-3">
              <div className="min-w-0">
                <div className="font-semibold truncate">{selected.subject}</div>
                <div className="text-xs text-[#6B6B70]">User: {selected.user_id}</div>
              </div>
              {selected.status === "open" && (
                <button onClick={() => close(selected.ticket_id)} className="text-xs px-2 py-1 rounded-lg bg-red-100 text-red-700 hover:bg-red-200">
                  Close
                </button>
              )}
            </div>
            <div className="flex-1 overflow-y-auto space-y-2 mb-3">
              {messages.map((m) => {
                const isAdmin = m.sender_id !== selected.user_id;
                return (
                  <div key={m.message_id} className={`flex ${isAdmin ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${
                      isAdmin ? "bg-purple-600 text-white" : "bg-[#F5F3EE]"
                    }`}>
                      <p>{m.content}</p>
                      <div className={`text-[10px] mt-1 ${isAdmin ? "text-white/60" : "text-[#6B6B70]"}`}>
                        {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {selected.status === "open" && (
              <div className="flex gap-2">
                <input
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendReply()}
                  placeholder="Reply…"
                  className="neo-input flex-1"
                />
                <button onClick={sendReply} disabled={!reply.trim() || sending} className="neo-btn bg-purple-600 border-purple-600">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function FeedbackPanel() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyId, setReplyId] = useState(null);
  const [replyMsg, setReplyMsg] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/feedback");
      setList(res.data || []);
    } catch { toast.error("Failed"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!replyId || !replyMsg.trim()) return;
    setSending(true);
    try {
      await api.post(`/admin/feedback/${replyId}/reply`, { message: replyMsg.trim() });
      toast.success("Reply sent");
      setReplyId(null);
      setReplyMsg("");
      load();
    } catch { toast.error("Failed"); }
    finally { setSending(false); }
  };

  if (loading) return <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-purple-600 mx-auto" /></div>;
  if (list.length === 0) return <div className="bg-white border rounded-2xl p-10 text-center text-[#6B6B70]">No feedback yet.</div>;

  return (
    <div className="space-y-3">
      {list.map((fb) => (
        <div key={fb.feedback_id} className="bg-white border border-[#E7E5E0] rounded-xl p-4 flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                fb.type === "suggestion" ? "bg-amber-100 text-amber-700"
                : fb.type === "complaint" ? "bg-red-100 text-red-700"
                : fb.type === "bug" ? "bg-purple-100 text-purple-700"
                : "bg-blue-100 text-blue-700"
              }`}>
                {fb.type}
              </span>
              {fb.resolved && <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">Resolved</span>}
              <span className="text-xs text-[#6B6B70]">{fb.user_id}</span>
            </div>
            <p className="text-sm mt-2 whitespace-pre-wrap">{fb.message}</p>
            <p className="text-xs text-[#6B6B70] mt-1">{new Date(fb.created_at).toLocaleString()}</p>
          </div>
          <button
            onClick={() => { setReplyId(fb.feedback_id); setReplyMsg(""); }}
            className="text-xs px-3 py-1.5 rounded-lg bg-purple-600 text-white hover:bg-purple-700 flex items-center gap-1 flex-shrink-0"
          >
            <Reply className="w-3 h-3" /> Reply
          </button>
        </div>
      ))}

      {replyId && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setReplyId(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold mb-3">Reply to feedback</h3>
            <textarea value={replyMsg} onChange={(e) => setReplyMsg(e.target.value)} className="neo-input h-32 resize-none mb-3" placeholder="Your reply…" />
            <div className="flex gap-2">
              <button onClick={() => setReplyId(null)} className="flex-1 py-2.5 rounded-xl border border-[#E7E5E0] text-sm">Cancel</button>
              <button onClick={send} disabled={sending || !replyMsg.trim()} className="flex-1 py-2.5 rounded-xl bg-purple-600 text-white text-sm disabled:opacity-50">
                {sending ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DsrPanel() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/dsr-requests");
      setList(res.data || []);
    } catch { toast.error("Failed"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const update = async (id, status, notes) => {
    try {
      await api.put(`/admin/dsr-requests/${id}`, { status, admin_notes: notes || "" });
      toast.success("Updated");
      load();
    } catch { toast.error("Failed"); }
  };

  if (loading) return <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-purple-600 mx-auto" /></div>;
  if (list.length === 0) return <div className="bg-white border rounded-2xl p-10 text-center text-[#6B6B70]">No data subject requests.</div>;

  const statusColor = {
    received: "bg-amber-100 text-amber-700",
    processing: "bg-blue-100 text-blue-700",
    fulfilled: "bg-green-100 text-green-700",
    rejected: "bg-red-100 text-red-700",
  };

  return (
    <div className="space-y-3">
      {list.map((d) => (
        <div key={d.request_id} className="bg-white border border-[#E7E5E0] rounded-xl p-4 space-y-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium uppercase ${statusColor[d.status] || "bg-gray-100"}`}>
                {d.status}
              </span>
              <span className="ml-2 text-xs text-[#6B6B70] capitalize">{d.kind}</span>
            </div>
            <span className="text-xs text-[#6B6B70]">{new Date(d.created_at).toLocaleString()}</span>
          </div>
          <div className="text-sm"><strong>User:</strong> {d.user_name || d.user_email}</div>
          {d.details && <div className="text-xs p-2 bg-[#F5F3EE] rounded-lg"><strong>Details:</strong> {d.details}</div>}
          {d.admin_notes && <div className="text-xs p-2 bg-blue-50 rounded-lg"><strong>Notes:</strong> {d.admin_notes}</div>}
          <div className="flex flex-wrap gap-1.5">
            {["processing", "fulfilled", "rejected"].map((s) => (
              <button
                key={s}
                onClick={() => update(d.request_id, s)}
                disabled={d.status === s}
                className="text-xs px-2 py-1 rounded-lg border border-[#E7E5E0] hover:bg-[#F5F3EE] disabled:opacity-40 capitalize"
              >
                Mark {s}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// =====================================================================
// ANALYTICS TAB
// =====================================================================
export function AnalyticsTab({ initialSub }) {
  const [sub, setSub] = useState(initialSub || "growth");
  useEffect(() => { if (initialSub) setSub(initialSub); }, [initialSub]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1.5">
        {[
          { key: "growth", label: "Growth" },
          { key: "engagement", label: "Engagement" },
          { key: "revenue", label: "Revenue" },
          { key: "retention", label: "Retention" },
        ].map((s) => (
          <button
            key={s.key}
            onClick={() => setSub(s.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium ${
              sub === s.key ? "bg-purple-600 text-white" : "bg-white border border-[#E7E5E0] hover:bg-[#F5F3EE]"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>
      {sub === "growth" && <GrowthPanel />}
      {sub === "engagement" && <EngagementPanel />}
      {sub === "revenue" && <RevenuePanel />}
      {sub === "retention" && <RetentionPanel />}
    </div>
  );
}

function GrowthPanel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/analytics/growth", { params: { days } })
      .then((res) => setData(res.data))
      .catch(() => toast.error("Failed"))
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <div className="space-y-4">
      <div className="flex gap-1.5">
        {[7, 30, 90, 365].map((d) => (
          <button key={d} onClick={() => setDays(d)} className={`px-3 py-1 rounded-full text-xs ${days === d ? "bg-purple-600 text-white" : "bg-white border border-[#E7E5E0]"}`}>
            {d === 365 ? "1y" : `${d}d`}
          </button>
        ))}
      </div>
      {loading ? <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-purple-600" /></div> : (
        <>
          <SectionCard title={`Signups — last ${days} days`}>
            <LineChart data={data?.signups || []} height={150} color="#7C3AED" />
          </SectionCard>
          <SectionCard title={`Active users — last ${days} days`}>
            <LineChart data={data?.active || []} height={150} color="#10B981" />
          </SectionCard>
        </>
      )}
    </div>
  );
}

function EngagementPanel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/analytics/engagement", { params: { days } })
      .then((res) => setData(res.data))
      .catch(() => toast.error("Failed"))
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <div className="space-y-4">
      <div className="flex gap-1.5">
        {[7, 30, 90].map((d) => (
          <button key={d} onClick={() => setDays(d)} className={`px-3 py-1 rounded-full text-xs ${days === d ? "bg-purple-600 text-white" : "bg-white border border-[#E7E5E0]"}`}>
            {d}d
          </button>
        ))}
      </div>
      {loading ? <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-purple-600" /></div> : (
        <>
          <SectionCard title="Messages per day">
            <BarChart data={data?.messages || []} height={150} color="#7C3AED" />
          </SectionCard>
          <SectionCard title="New matches per day">
            <BarChart data={data?.matches || []} height={150} color="#EC4899" />
          </SectionCard>
        </>
      )}
    </div>
  );
}

function RevenuePanel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/analytics/revenue", { params: { days } })
      .then((res) => setData(res.data))
      .catch(() => toast.error("Failed"))
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <div className="space-y-4">
      <div className="flex gap-1.5">
        {[7, 30, 90].map((d) => (
          <button key={d} onClick={() => setDays(d)} className={`px-3 py-1 rounded-full text-xs ${days === d ? "bg-purple-600 text-white" : "bg-white border border-[#E7E5E0]"}`}>
            {d}d
          </button>
        ))}
      </div>
      {loading ? <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-purple-600" /></div> : (
        <>
          <div className="grid grid-cols-2 gap-4">
            <KpiCard label="Diamonds moved" value={data?.total_diamonds_moved ?? 0} icon={Gem} accent="purple" />
            <KpiCard label="Transactions" value={data?.total_transactions ?? 0} icon={Coins} accent="amber" />
          </div>
          <SectionCard title="Daily diamonds spent">
            <BarChart data={(data?.daily || []).map((d) => ({ cnt: d.diamonds }))} height={150} color="#F59E0B" />
          </SectionCard>
          <SectionCard title="Top spenders">
            <div className="divide-y divide-[#F5F3EE]">
              {(data?.top_spenders || []).map((s, i) => (
                <div key={s.user_id} className="flex items-center gap-3 py-2 text-sm">
                  <span className="w-5 text-[#6B6B70] text-xs tabular-nums">{i + 1}.</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{s.name || s.email}</div>
                    <div className="text-xs text-[#6B6B70] font-mono truncate">{s.user_id}</div>
                  </div>
                  <span className="font-bold tabular-nums">{s.total_spent}💎</span>
                </div>
              ))}
              {(!data?.top_spenders || data.top_spenders.length === 0) && (
                <div className="text-center text-xs text-[#6B6B70] py-4">No purchases yet</div>
              )}
            </div>
          </SectionCard>
        </>
      )}
    </div>
  );
}

function RetentionPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/analytics/retention")
      .then((res) => setData(res.data))
      .catch(() => toast.error("Failed"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-purple-600" /></div>;

  return (
    <SectionCard title="Retention by signup week" subtitle="Users who returned at least once within N weeks of signing up">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-[#6B6B70] uppercase">
            <tr>
              <th className="text-left p-2">Cohort</th>
              <th className="text-right p-2">Size</th>
              <th className="text-right p-2">Week 1</th>
              <th className="text-right p-2">Week 2</th>
              <th className="text-right p-2">Week 4</th>
            </tr>
          </thead>
          <tbody>
            {(data?.cohorts || []).map((c) => {
              const pct = (n) => c.size ? `${Math.round((n / c.size) * 100)}%` : "—";
              return (
                <tr key={c.cohort} className="border-t border-[#F5F3EE]">
                  <td className="p-2 text-xs">{fmtDate(c.cohort)}</td>
                  <td className="p-2 text-right tabular-nums">{c.size}</td>
                  <td className="p-2 text-right tabular-nums">{pct(c.w1)}</td>
                  <td className="p-2 text-right tabular-nums">{pct(c.w2)}</td>
                  <td className="p-2 text-right tabular-nums">{pct(c.w4)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

// =====================================================================
// SYSTEM TAB (includes FeaturesPanel — the share-rewards toggle)
// =====================================================================
export function SystemTab({ initialSub }) {
  const [sub, setSub] = useState(initialSub || "announce");
  useEffect(() => { if (initialSub) setSub(initialSub); }, [initialSub]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1.5">
        {[
          { key: "announce", label: "Announce" },
          { key: "version", label: "Version" },
          { key: "location", label: "Location fixer" },
          { key: "onboarding", label: "Onboarding cleanup" },
          { key: "features", label: "Feature flags" },
        ].map((s) => (
          <button
            key={s.key}
            onClick={() => setSub(s.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium ${
              sub === s.key ? "bg-purple-600 text-white" : "bg-white border border-[#E7E5E0] hover:bg-[#F5F3EE]"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>
      {sub === "announce" && <AnnouncePanel />}
      {sub === "version" && <VersionPanel />}
      {sub === "location" && <LocationFixerPanel />}
      {sub === "onboarding" && <OnboardingPanel />}
      {sub === "features" && <FeaturesPanel />}
    </div>
  );
}

function AnnouncePanel() {
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!msg.trim()) return;
    if (!window.confirm(`Send to all users?`)) return;
    setSending(true);
    try {
      const res = await api.post("/admin/announce", { message: msg.trim() });
      toast.success(`Sent to ${res.data.sent_to} users`);
      setMsg("");
    } catch { toast.error("Failed"); }
    finally { setSending(false); }
  };

  return (
    <SectionCard title="Send announcement" subtitle="Every active user receives a notification + push">
      <textarea value={msg} onChange={(e) => setMsg(e.target.value)} className="neo-input h-32 resize-none mb-3" placeholder="Your message…" />
      <button onClick={send} disabled={sending || !msg.trim()} className="neo-btn bg-purple-600 border-purple-600 w-full">
        {sending ? "Sending…" : "Send to all users"}
      </button>
    </SectionCard>
  );
}

function VersionPanel() {
  const [current, setCurrent] = useState(null);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/version").then((r) => setCurrent(r.data.min_app_version)).catch(() => {});
  }, []);

  const save = async () => {
    const v = parseInt(input, 10);
    if (!v || v < 1) { toast.error("Must be ≥ 1"); return; }
    if (!window.confirm(`Set minimum app version to ${v}?\n\nOlder builds will be blocked. Make sure the new build is live first.`)) return;
    setSaving(true);
    try {
      const res = await api.post("/admin/version", { min_app_version: v });
      setCurrent(res.data.min_app_version);
      setInput("");
      toast.success("Updated");
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  return (
    <SectionCard title="Minimum app version" subtitle="Blocks users on older builds until they update">
      <div className="mb-4 p-3 bg-[#F5F3EE] rounded-lg">
        <div className="text-xs text-[#6B6B70]">Current minimum</div>
        <div className="text-3xl font-bold text-purple-600 tabular-nums">{current ?? "—"}</div>
      </div>
      <div className="flex gap-2">
        <input type="number" min="1" value={input} onChange={(e) => setInput(e.target.value)} className="neo-input flex-1" placeholder="New version" />
        <button onClick={save} disabled={saving || !input} className="neo-btn bg-purple-600 border-purple-600">
          {saving ? "Saving…" : "Update"}
        </button>
      </div>
    </SectionCard>
  );
}

function LocationFixerPanel() {
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/location-fixer/scan");
      setScan(res.data);
    } catch { toast.error("Scan failed"); }
    finally { setLoading(false); }
  };

  const fix = async () => {
    if (!scan || scan.needs_fix === 0) return;
    if (!window.confirm(`Fix ${scan.needs_fix} profile(s)? May take a while.`)) return;
    setLoading(true);
    try {
      const res = await api.post("/admin/location-fixer/run");
      toast.success(`Fixed ${res.data.fixed}, failed ${res.data.failed}`);
      run();
    } catch { toast.error("Failed"); }
    finally { setLoading(false); }
  };

  return (
    <SectionCard title="Location fixer" subtitle="Find profiles with GPS but no country/city and reverse-geocode them">
      <div className="flex flex-wrap gap-2 mb-3">
        <button onClick={run} disabled={loading} className="neo-btn bg-purple-600 border-purple-600 text-sm">
          {loading ? "Scanning…" : "Scan"}
        </button>
        <button onClick={fix} disabled={loading || !scan || scan.needs_fix === 0} className="neo-btn bg-green-600 border-green-600 text-sm disabled:opacity-50">
          Run fixer
        </button>
      </div>
      {scan && (
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="p-2 bg-[#F5F3EE] rounded">Total with GPS: {scan.scanned}</div>
          <div className="p-2 bg-amber-50 rounded text-amber-700">Need fixing: {scan.needs_fix}</div>
        </div>
      )}
    </SectionCard>
  );
}

function OnboardingPanel() {
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/onboarding-scan");
      setScan(res.data);
    } catch { toast.error("Scan failed"); }
    finally { setLoading(false); }
  };

  const purge = async () => {
    if (!scan || scan.needs_delete === 0) return;
    if (!window.confirm(`Permanently delete ${scan.needs_delete} user(s) with incomplete onboarding?`)) return;
    setLoading(true);
    try {
      const res = await api.post("/admin/onboarding-purge");
      toast.success(`Deleted ${res.data.deleted}, failed ${res.data.failed}`);
      run();
    } catch { toast.error("Failed"); }
    finally { setLoading(false); }
  };

  return (
    <SectionCard title="Onboarding cleanup" subtitle="Delete accounts that never finished onboarding (admins & system user excluded)">
      <div className="flex flex-wrap gap-2 mb-3">
        <button onClick={run} disabled={loading} className="neo-btn bg-purple-600 border-purple-600 text-sm">
          {loading ? "Scanning…" : "Scan"}
        </button>
        <button onClick={purge} disabled={loading || !scan || scan.needs_delete === 0} className="neo-btn bg-red-600 border-red-600 text-sm disabled:opacity-50">
          Delete all incomplete
        </button>
      </div>
      {scan && (
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="p-2 bg-[#F5F3EE] rounded">Incomplete: {scan.scanned}</div>
          <div className="p-2 bg-amber-50 rounded text-amber-700">Will delete: {scan.needs_delete}</div>
        </div>
      )}
    </SectionCard>
  );
}

function FeaturesPanel() {
  const [shareEnabled, setShareEnabled] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/features/share-claims");
      setShareEnabled(res.data.enabled === true);
    } catch {
      toast.error("Failed to load feature flags");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    if (shareEnabled == null) return;
    const next = !shareEnabled;
    if (
      !window.confirm(
        next
          ? "Turn ON the share-and-earn-100-tokens feature?\n\nUsers will be able to submit new claims immediately."
          : "Turn OFF the share-and-earn-100-tokens feature?\n\nNew claims will be rejected. Existing pending claims can still be reviewed.",
      )
    )
      return;

    setSaving(true);
    try {
      await api.post("/admin/features/share-claims", { enabled: next });
      setShareEnabled(next);
      toast.success(
        next ? "Share rewards enabled" : "Share rewards disabled",
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-purple-600 mx-auto" />
      </div>
    );
  }

  return (
    <SectionCard
      title="Feature flags"
      subtitle="Toggle features on or off without redeploying. Changes take effect immediately."
    >
      <div className="flex items-center justify-between gap-4 p-4 rounded-xl border border-[#E7E5E0]">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-pink-600" />
            <div className="font-semibold text-sm">
              Social share — earn 100 tokens
            </div>
            <span
              className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                shareEnabled
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {shareEnabled ? "ON" : "OFF"}
            </span>
          </div>
          <p className="text-xs text-[#6B6B70] mt-1 leading-relaxed">
            When <strong>on</strong>: users see the /earn-tokens/share page and
            can submit claims. When <strong>off</strong>: the page shows
            "temporarily paused" and new claims are rejected — existing pending
            claims can still be reviewed and approved here.
          </p>
        </div>

        <button
          onClick={toggle}
          disabled={saving}
          className={`relative flex-shrink-0 inline-flex h-7 w-12 items-center rounded-full transition-colors ${
            shareEnabled ? "bg-emerald-500" : "bg-gray-300"
          } ${saving ? "opacity-50 cursor-wait" : ""}`}
          aria-label={shareEnabled ? "Disable share rewards" : "Enable share rewards"}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              shareEnabled ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>

      <div className="mt-3 p-3 rounded-lg bg-[#FAFAF7] border border-[#E7E5E0]">
        <div className="text-xs text-[#6B6B70] leading-relaxed">
          <strong className="text-[#0F0F10]">How it works:</strong> the flag is
          stored in the <code className="font-mono text-[10px] bg-white px-1 py-0.5 rounded border">
          app_config</code> table as <code className="font-mono text-[10px] bg-white px-1 py-0.5 rounded border">
          share_claims_enabled</code>. No restart needed — every request reads
          the flag live, so it works for the web <em>and</em> the Flutter app.
        </div>
      </div>
    </SectionCard>
  );
}